import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Banknote, ShieldCheck, Clock, Car, Users, Building2, Phone } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api, kina, ROLE_HOME } from "@/lib/api";
import { PLACES } from "@/constants/places";
import { InstallAppButton } from "@/components/InstallApp";
import { LiveStats } from "@/components/LiveStats";
import { usePoll } from "@/lib/usePoll";

const NavBar = () => {
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0B192C]/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-12">
        <Logo dark />
        <nav className="hidden items-center gap-8 text-sm font-medium text-blue-100 md:flex">
          <a href="#how" className="transition-colors hover:text-amber-400" data-testid="nav-how-link">How it works</a>
          <a href="#drivers" className="transition-colors hover:text-amber-400" data-testid="nav-drivers-link">For drivers</a>
          <a href="#fleet" className="transition-colors hover:text-amber-400" data-testid="nav-fleet-link">For the fleet</a>
          <InstallAppButton className="rounded-full border border-white/20 px-3 py-1.5 text-white transition-colors hover:border-amber-400 hover:text-amber-400" />
        </nav>
        {user ? (
          <Link to={ROLE_HOME[user.role]} data-testid="nav-dashboard-button" className="rounded-full bg-amber-400 px-5 py-2 text-sm font-bold text-slate-900 transition-transform hover:scale-[1.03]">
            My dashboard
          </Link>
        ) : (
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="nav-login-button" className="rounded-full px-4 py-2 text-sm font-semibold text-white transition-colors hover:text-amber-400">Sign in</Link>
            <Link to="/register" data-testid="nav-register-button" className="rounded-full bg-amber-400 px-5 py-2 text-sm font-bold text-slate-900 transition-transform hover:scale-[1.03]">Book a ride</Link>
          </div>
        )}
      </div>
    </header>
  );
};

const Hero = ({ settings, stats }) => (
  <section className="grain relative overflow-hidden bg-[#0B192C]">
    <div className="absolute -left-40 top-20 h-[480px] w-[480px] rounded-full bg-blue-600/30 blur-[120px]" />
    <div className="relative mx-auto grid max-w-7xl gap-12 px-4 pb-20 pt-14 sm:px-6 lg:grid-cols-12 lg:px-12 lg:pb-28 lg:pt-20">
      <div className="lg:col-span-6">
        <p className="fade-up inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-amber-400">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> Now online in Port Moresby
        </p>
        <h1 className="fade-up mt-6 text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" style={{ animationDelay: "0.1s" }}>
          The blue taxi you trust, <span className="text-amber-400">now one tap away.</span>
        </h1>
        <p className="fade-up mt-6 max-w-xl text-base leading-relaxed text-blue-100/80 sm:text-lg" style={{ animationDelay: "0.2s" }}>
          Book a Comfort Taxi anywhere in Port Moresby — from Jacksons Airport to Waigani, Boroko to Gerehu. See your fare before you ride and pay the driver in cash.
        </p>
        <div className="fade-up mt-9 flex flex-wrap gap-3" style={{ animationDelay: "0.3s" }}>
          <Link to="/register" data-testid="hero-book-ride-button" className="group inline-flex items-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 font-semibold text-white shadow-lg shadow-blue-600/30 transition-all hover:bg-blue-500 active:scale-[0.98]">
            Book your ride <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <InstallAppButton label="Install the app" className="rounded-full px-4 py-3.5 font-semibold text-amber-400 underline-offset-4 hover:underline md:hidden" />
        </div>
        <dl className="fade-up mt-12 grid max-w-lg grid-cols-3 gap-6 border-t border-white/10 pt-8" style={{ animationDelay: "0.4s" }}>
          {[
            ["Base fare", kina(settings?.base_fare)],
            ["Per km", kina(settings?.per_km)],
            ["Trips done", stats?.trips ?? "—"],
          ].map(([k, v]) => (
            <div key={k}>
              <dt className="text-[11px] font-bold uppercase tracking-widest text-blue-300">{k}</dt>
              <dd className="mt-1 font-mono-num text-xl font-semibold text-white" data-testid={`hero-stat-${k.replace(/\s/g, "-").toLowerCase()}`}>{v}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="fade-up relative lg:col-span-6" style={{ animationDelay: "0.25s" }}>
        <div className="relative overflow-hidden rounded-[2rem] border border-white/10 shadow-2xl shadow-black/40">
          <img src="/img/comfort-taxi.jpg" alt="Comfort Taxi driver with a blue taxi in Port Moresby" className="h-[360px] w-full object-cover sm:h-[460px]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C]/80 via-transparent to-transparent" />
        </div>
        <div className="absolute -bottom-6 left-4 right-4 rounded-2xl border border-blue-100 bg-white/95 p-4 shadow-xl backdrop-blur-xl sm:left-8 sm:right-auto sm:w-80">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-700"><MapPin className="h-5 w-5" /></span>
            <div className="min-w-0">
              <p className="text-xs text-slate-500">Jacksons Airport → Waigani</p>
              <p className="font-semibold text-slate-900">Upfront fare, paid in cash</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const HOW = [
  { icon: MapPin, t: "Pin your pickup", d: "Choose a landmark or tap the map — Boroko, Gordons, Town, anywhere in POM." },
  { icon: Banknote, t: "See the fare first", d: "Distance-based pricing in Kina. No surprises when you arrive." },
  { icon: Car, t: "A blue cab arrives", d: "A verified Comfort Taxi driver accepts and comes to you." },
  { icon: ShieldCheck, t: "Ride & pay cash", d: "Pay the driver directly. Every trip is recorded on the platform." },
];

const HowItWorks = () => (
  <section id="how" className="bg-white px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
    <div className="mx-auto max-w-7xl">
      <p className="text-xs font-bold uppercase tracking-widest text-blue-600">How it works</p>
      <h2 className="mt-3 max-w-2xl text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Four steps from the kerb to your destination.</h2>
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {HOW.map((s, i) => (
          <div key={s.t} className="group rounded-2xl border border-blue-100 bg-[#F4F8FD] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-blue-300 hover:bg-white hover:shadow-xl hover:shadow-blue-900/5">
            <div className="flex items-center justify-between">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-700 text-white transition-colors group-hover:bg-amber-400 group-hover:text-slate-900"><s.icon className="h-5 w-5" /></span>
              <span className="font-mono-num text-sm text-slate-300">0{i + 1}</span>
            </div>
            <h3 className="mt-6 text-lg font-semibold text-slate-900">{s.t}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Split = () => (
  <section className="bg-[#F4F8FD] px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
    <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-5">
      <div id="drivers" className="relative overflow-hidden rounded-[2rem] bg-blue-700 p-8 text-white sm:p-12 lg:col-span-3">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-blue-400/30 blur-3xl" />
        <Users className="relative h-8 w-8 text-amber-400" />
        <h2 className="relative mt-6 text-3xl font-bold tracking-tight sm:text-4xl">For Comfort Taxi drivers.</h2>
        <p className="relative mt-4 max-w-lg text-blue-100">
          Driver accounts are created by the Comfort Taxi Super Admin and Dispatch Team for our own drivers. Once your account and taxi are set up, sign in, go online and receive ride requests. Keep 95% of every fare — a small 5% daily platform fee covers the service.
        </p>
        <Link to="/login" data-testid="drivers-signin-button" className="relative mt-8 inline-flex items-center gap-2 rounded-full bg-amber-400 px-6 py-3 font-bold text-slate-900 transition-transform hover:scale-[1.03]">
          Driver sign in <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div id="fleet" className="rounded-[2rem] border border-blue-100 bg-white p-8 sm:p-12 lg:col-span-2">
        <Building2 className="h-8 w-8 text-blue-700" />
        <h2 className="mt-6 text-2xl font-bold tracking-tight text-slate-900">One hub for the whole fleet.</h2>
        <ul className="mt-6 space-y-3 text-sm text-slate-600">
          {["Register Comfort Taxi vehicles & assign drivers", "Add Comfort Taxi drivers and manage shifts", "Live view of every booking in the city", "Daily earnings & fee statements"].map((t) => (
            <li key={t} className="flex gap-3"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />{t}</li>
          ))}
        </ul>
        <Link to="/login" data-testid="fleet-login-button" className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-blue-700 hover:text-blue-900">
          Fleet staff sign in <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </section>
);

const Coverage = () => (
  <section className="bg-white px-4 py-20 sm:px-6 lg:px-12">
    <div className="mx-auto max-w-7xl">
      <p className="text-xs font-bold uppercase tracking-widest text-blue-600">Coverage</p>
      <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Across the National Capital District.</h2>
      <div className="mt-10 flex flex-wrap gap-2.5">
        {PLACES.map((p) => (
          <span key={p.name} className="rounded-full border border-blue-100 bg-[#F4F8FD] px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-blue-600 hover:text-blue-700">{p.name}</span>
        ))}
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="bg-[#0B192C] px-4 py-12 text-blue-200 sm:px-6 lg:px-12">
    <div className="mx-auto flex max-w-7xl flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
      <Logo dark />
      <p className="flex items-center gap-2 text-sm"><Phone className="h-4 w-4 text-amber-400" /> Toll free 180 2900</p>
      <p className="text-xs">© {new Date().getFullYear()} Comfort Taxi Platform · Port Moresby, PNG</p>
    </div>
  </footer>
);

export default function Landing() {
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  usePoll(() => api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {}), 15000, []);
  return (
    <div data-testid="landing-page">
      <NavBar />
      <Hero settings={settings} stats={stats} />
      <LiveStats stats={stats} />
      <HowItWorks />
      <Split />
      <Coverage />
      <Footer />
    </div>
  );
}
